/* eslint-disable react-refresh/only-export-components */
/**
 * High Contrast Mode Provider
 *
 * Detects the OS-level prefers-contrast media query and provides a manual
 * toggle for users who need enhanced visual contrast. When active, adds a
 * `data-high-contrast` attribute to the document root, which Tailwind
 * variants can target.
 *
 * Part of Tech Stack Opportunity Analysis Item 3e:
 * "high contrast mode"
 *
 * @usage
 * ```tsx
 * // Wrap app:
 * <HighContrastProvider>
 *   <App />
 * </HighContrastProvider>
 *
 * // In components:
 * const { isHighContrast, toggleHighContrast } = useHighContrast();
 *
 * // In Tailwind CSS:
 * // [data-high-contrast="true"] .text-muted-foreground {
 * //   @apply text-foreground;
 * // }
 * ```
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';

interface HighContrastContextValue {
  /** Whether high contrast mode is active (OS or manual) */
  isHighContrast: boolean;
  /** Whether user has manually toggled high contrast */
  isManualOverride: boolean;
  /** Whether the OS prefers high contrast */
  osPreference: boolean;
  /** Toggle high contrast mode manually */
  toggleHighContrast: () => void;
}

const HighContrastContext = createContext<HighContrastContextValue>({
  isHighContrast: false,
  isManualOverride: false,
  osPreference: false,
  toggleHighContrast: () => {},
});

const STORAGE_KEY = 'forge-high-contrast';

export function HighContrastProvider({ children }: { children: ReactNode }) {
  // Detect OS preference
  const [osPreference, setOsPreference] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(prefers-contrast: more)')?.matches ?? false;
  });

  // Manual override (persisted)
  const [manualOverride, setManualOverride] = useState<boolean | null>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored !== null ? stored === 'true' : null;
    } catch {
      return null;
    }
  });

  // Listen for OS changes
  useEffect(() => {
    const mql = window.matchMedia?.('(prefers-contrast: more)');
    if (!mql) return;

    const handler = (e: MediaQueryListEvent) => setOsPreference(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Resolved state: manual override wins, then OS preference
  const isHighContrast = manualOverride !== null ? manualOverride : osPreference;

  // Sync to document attribute
  useEffect(() => {
    document.documentElement.setAttribute('data-high-contrast', String(isHighContrast));
    return () => {
      document.documentElement.removeAttribute('data-high-contrast');
    };
  }, [isHighContrast]);

  const toggleHighContrast = useCallback(() => {
    setManualOverride((prev) => {
      const next = prev === null ? !osPreference : !prev;
      try {
        localStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Ignore storage errors
      }
      return next;
    });
  }, [osPreference]);

  return (
    <HighContrastContext.Provider
      value={{
        isHighContrast,
        isManualOverride: manualOverride !== null,
        osPreference,
        toggleHighContrast,
      }}
    >
      {children}
    </HighContrastContext.Provider>
  );
}

export function useHighContrast() {
  return useContext(HighContrastContext);
}